import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  Headset,
  Maximize2,
  MessageCircle,
  Minimize2
} from 'lucide-react';
import { streamAIResponse } from '../../services/aiService';
import { useOcean } from '../../contexts/OceanDataContext';
import ExerciseCommsPanel from '../panels/ExerciseCommsPanel';
import ChatChart from './ChatChart';
import { resolveChartForConversation } from '../../utils/chatChartBuilder';
import {
  parseUserFilterIntent,
  buildPlatformActionConfirmation
} from '../../utils/chatPlatformActions';

const WELCOME_MESSAGE = 'Hello! How can I help you analyze your ocean data today?';

const markdownComponents = {
  p: ({ children }) => <p className="mb-3 last:mb-0 leading-relaxed text-slate-700">{children}</p>,
  ul: ({ children }) => <ul className="list-disc list-outside ml-4 mb-3 space-y-1 text-slate-700">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-outside ml-4 mb-3 space-y-1 text-slate-700">{children}</ol>,
  li: ({ children }) => <li className="text-slate-700">{children}</li>,
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
      {children}
    </a>
  ),
  code: ({ inline, children }) => (
    inline
      ? <code className="bg-slate-100 px-1.5 py-0.5 rounded text-[0.85em] font-mono text-slate-800">{children}</code>
      : <code className="block bg-slate-100 p-3 rounded-lg text-xs font-mono text-slate-800 overflow-x-auto mb-3">{children}</code>
  ),
  pre: ({ children }) => <pre className="mb-3">{children}</pre>,
  strong: ({ children }) => <strong className="font-semibold text-slate-900">{children}</strong>,
  em: ({ children }) => <em className="italic text-slate-600">{children}</em>,
  h1: ({ children }) => <h1 className="text-base font-bold text-slate-900 mb-2 mt-1">{children}</h1>,
  h2: ({ children }) => <h2 className="text-sm font-semibold text-slate-900 mb-2 mt-1">{children}</h2>,
  h3: ({ children }) => <h3 className="text-sm font-medium text-slate-800 mb-1 mt-1">{children}</h3>,
  img: ({ src, alt }) => (
    <img
      src={src}
      alt={alt || 'Chart'}
      className="my-3 max-w-full rounded-xl border border-slate-200 bg-white"
    />
  )
};

const Chatbot = ({
  panelMode = 'assistant',
  onPanelModeChange,
  isPanelCollapsed = false,
  onTogglePanelCollapse,
  timeSeriesData = [],
  data = [],
  dataSource = 'simulated',
  selectedDepth = 0,
  selectedArea = '',
  selectedModel = 'NGOSF2',
  selectedParameter = 'Current Speed',
  playbackSpeed = 0.1,
  currentFrame = 0,
  envData = {},
  startDate,
  endDate,
  dataLoaded
}) => {
  const isCommsMode = panelMode === 'comms';
  const ocean = useOcean();
  const {
    chatMessages,
    isTyping,
    addUserMessage,
    addAIResponse,
    addStreamingAIResponse,
    appendToMessage,
    finalizeStreamingMessage,
    updateMessage,
    getThreadId,
    setThreadId,
    startTyping,
    stopTyping,
    removeMessage,
    clearChatMessages
  } = ocean;

  const [inputMessage, setInputMessage] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);

  const chatEndRef = useRef(null);
  const isInitializedRef = useRef(false);
  const streamingMessageIdRef = useRef(null);
  const pendingChunksRef = useRef('');
  const flushFrameRef = useRef(null);
  const lastPlatformSettingsRef = useRef('');
  const maxRetries = 2;

  const applyPlatformActions = useCallback((settings) => {
    if (!settings || !ocean.fetchData) return;

    const key = JSON.stringify(settings);
    if (key === lastPlatformSettingsRef.current) return;
    lastPlatformSettingsRef.current = key;

    ocean.fetchData(settings);
  }, [ocean]);

  const queueStreamChunk = useCallback((chunk) => {
    if (!chunk) return;
    pendingChunksRef.current += chunk;
    if (flushFrameRef.current) return;

    flushFrameRef.current = requestAnimationFrame(() => {
      const pending = pendingChunksRef.current;
      pendingChunksRef.current = '';
      flushFrameRef.current = null;
      const messageId = streamingMessageIdRef.current;
      if (pending && messageId) {
        appendToMessage(messageId, pending);
      }
    });
  }, [appendToMessage]);

  const revealTextIncrementally = useCallback(async (messageId, text) => {
    const tokens = text.match(/\S+\s*|\n/g) || [text];
    let built = '';
    for (const token of tokens) {
      built += token;
      updateMessage(messageId, { content: built });
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, 28));
    }
    updateMessage(messageId, { content: text });
  }, [updateMessage]);

  useEffect(() => {
    if (!dataLoaded || !startDate || !endDate) return;
    if (isInitializedRef.current || chatMessages.length > 0) return;

    isInitializedRef.current = true;
    addAIResponse(WELCOME_MESSAGE, 'local');
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: run once when data/dates ready
  }, [dataLoaded, startDate, endDate]);

  useEffect(() => () => {
    if (flushFrameRef.current) {
      cancelAnimationFrame(flushFrameRef.current);
    }
  }, []);

  const handleRefreshChat = () => {
    clearChatMessages();
    setThreadId(null);
    isInitializedRef.current = true;
    addAIResponse(WELCOME_MESSAGE, 'local');
  };

  const sendMessage = async () => {
    if (!inputMessage.trim()) return;

    addUserMessage(inputMessage);
    const currentInput = inputMessage;
    setInputMessage('');
    startTyping('Analyzing...');
    setRetryCount(0);

    try {
      await processAIResponse(currentInput);
    } catch (error) {
      console.error('Failed to process AI response:', error);
      addErrorMessage('Sorry, I encountered an error processing your request. Please try again.');
    } finally {
      stopTyping();
    }
  };

  const processAIResponse = async (message, retryAttempt = 0) => {
    let streamingMessage = null;
    let localFilterIntent = null;

    try {
      if (retryAttempt === 0) {
        localFilterIntent = parseUserFilterIntent(message);
        if (localFilterIntent) {
          applyPlatformActions(localFilterIntent);
        }
      }

      const { startDate: apiStart, endDate: apiEnd } = ocean.getDateRangeForApi?.() ?? { startDate, endDate };
      const context = {
        currentData: timeSeriesData && timeSeriesData.length > 0 ? timeSeriesData[timeSeriesData.length - 1] : null,
        timeSeriesData,
        dataSource,
        selectedDepth,
        selectedModel,
        selectedParameter,
        selectedArea,
        playbackSpeed,
        currentFrame,
        totalFrames: data?.length || 24,
        startDate: apiStart,
        endDate: apiEnd,
        envData
      };

      const threadId = getThreadId();
      streamingMessage = addStreamingAIResponse('api', { retryAttempt });
      streamingMessageIdRef.current = streamingMessage.id;

      const { text: aiResponse, threadId: newThreadId, streamedIncrementally, images = [] } = await streamAIResponse(
        message,
        context,
        threadId,
        {
          onChunk: (chunk) => {
            stopTyping();
            queueStreamChunk(chunk);
          },
          onThreadId: (id) => {
            setThreadId(id);
          },
          onPlatformActions: applyPlatformActions,
          onImages: (nextImages) => {
            if (streamingMessage?.id && nextImages?.length) {
              updateMessage(streamingMessage.id, {
                metadata: { ...streamingMessage.metadata, images: nextImages, streaming: true }
              });
            }
          }
        }
      );

      if (flushFrameRef.current) {
        cancelAnimationFrame(flushFrameRef.current);
        flushFrameRef.current = null;
      }
      if (pendingChunksRef.current && streamingMessage?.id) {
        appendToMessage(streamingMessage.id, pendingChunksRef.current);
        pendingChunksRef.current = '';
      }

      if (newThreadId) {
        setThreadId(newThreadId);
      }

      if (!aiResponse || aiResponse.includes('[Local Response')) {
        throw new Error('API not available');
      }

      stopTyping();
      const conversationText = [
        ...chatMessages.filter((entry) => entry.isUser).slice(-3).map((entry) => entry.content),
        message
      ].join('\n');
      const clientChart = resolveChartForConversation({
        userMessage: conversationText,
        assistantMessage: aiResponse,
        rows: data,
        apiImages: images
      });

      if (streamedIncrementally) {
        updateMessage(streamingMessage.id, {
          content: aiResponse,
          metadata: {
            ...streamingMessage.metadata,
            images,
            chart: clientChart,
            streaming: false
          }
        });
      } else {
        await revealTextIncrementally(streamingMessage.id, aiResponse);
        updateMessage(streamingMessage.id, {
          metadata: {
            ...streamingMessage.metadata,
            images,
            chart: clientChart,
            streaming: false
          }
        });
      }

      finalizeStreamingMessage(streamingMessage.id, { retryAttempt, images, chart: clientChart });
      streamingMessageIdRef.current = null;
      setRetryCount(0);
    } catch (error) {
      console.error(`AI response attempt ${retryAttempt + 1} failed:`, error);
      streamingMessageIdRef.current = null;

      if (streamingMessage?.id) {
        removeMessage(streamingMessage.id);
      }

      if (retryAttempt < maxRetries) {
        setRetryCount(retryAttempt + 1);
        const delay = Math.pow(2, retryAttempt) * 1000;
        setTimeout(() => {
          processAIResponse(message, retryAttempt + 1);
        }, delay);
      } else {
        addErrorMessage(
          localFilterIntent
            ? (buildPlatformActionConfirmation(localFilterIntent)
              || 'Updated the control panel, but the chat service did not return a reply. Verify REACT_APP_BLUETIDE_SIDECAR_UUID if this keeps happening.')
            : 'The chat service returned no response. Map data is working, but the staging chat/LLM stream is empty — verify REACT_APP_BLUETIDE_SIDECAR_UUID and check with the backend team.'
        );
      }
    }
  };

  const addErrorMessage = (errorText) => {
    addAIResponse(errorText, 'error');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleExpandChat = () => {
    setIsExpanded(true);
  };

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [chatMessages, isTyping]);

  const renderAssistantBubble = (content, { isStreaming = false, isError = false, images = [], chart = null } = {}) => (
    <div className={`rounded-2xl px-4 py-3 shadow-sm text-sm leading-relaxed ${
      isError ? 'bg-red-50 text-red-800' : 'bg-white text-slate-700'
    }`}>
      <div className="markdown-content">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
          {content}
        </ReactMarkdown>
        {images.map((src, index) => (
          <img
            key={`${src.slice(0, 32)}-${index}`}
            src={src}
            alt={`Chart ${index + 1}`}
            className="mt-3 max-w-full rounded-xl border border-slate-200 bg-white"
          />
        ))}
        {chart && <ChatChart chart={chart} />}
        {isStreaming && (
          <span className="inline-block w-0.5 h-4 ml-0.5 bg-amber-500 animate-pulse align-middle" />
        )}
      </div>
    </div>
  );

  const renderMessages = (expanded = false) => (
    <div className={`flex-1 overflow-y-auto bg-slate-100/90 ${expanded ? 'px-5 py-4' : 'px-3 py-4 md:px-4'}`}>
      <div className="space-y-4">
        {chatMessages.map((msg) => {
          if (!msg.isUser && !msg.content && msg.metadata?.streaming) {
            return null;
          }

          if (msg.isUser) {
            return (
              <div key={msg.id} className="flex justify-end">
                <div className={`max-w-[88%] rounded-2xl bg-[#c6d7e7] px-4 py-2.5 text-slate-900 shadow-sm ${
                  expanded ? 'text-sm' : 'text-[13px]'
                }`}>
                  <p className="leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            );
          }

          return (
            <div key={msg.id} className="max-w-[92%]">
              {renderAssistantBubble(msg.content, {
                isStreaming: msg.metadata?.streaming,
                isError: msg.source === 'error',
                images: msg.metadata?.images || [],
                chart: msg.metadata?.chart || null
              })}
            </div>
          );
        })}

        {isTyping && (
          <div className="max-w-[92%]">
            <div className="rounded-2xl bg-white px-4 py-3 shadow-sm">
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:0ms]" />
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:150ms]" />
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:300ms]" />
                </div>
                <span className="text-xs text-slate-500">
                  {retryCount > 0 ? `Retrying (${retryCount}/${maxRetries})...` : 'Thinking...'}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
      <div ref={chatEndRef} />
    </div>
  );

  const renderInput = (expanded = false) => (
    <div className={`bg-white ${expanded ? 'px-5 pb-5 pt-3' : 'px-3 pb-3 pt-2 md:px-4 md:pb-4'}`}>
      <div
        className={`relative flex items-center rounded-full bg-slate-100 ${
          expanded ? 'h-12' : 'h-11'
        }`}
      >
        <input
          type="text"
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message"
          disabled={isTyping}
          className={`min-w-0 flex-1 rounded-full border-0 bg-transparent pl-4 pr-12 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-0 disabled:opacity-60 ${
            expanded ? 'text-sm' : 'text-[13px] md:text-sm'
          }`}
        />
        <button
          type="button"
          onClick={sendMessage}
          disabled={!inputMessage.trim() || isTyping}
          aria-label="Send message"
          className="absolute right-1.5 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-slate-900 text-white transition-colors hover:bg-black disabled:cursor-not-allowed disabled:bg-slate-300"
        >
          <ArrowUp className="h-4 w-4" strokeWidth={2.5} />
        </button>
      </div>
      <p className="mt-3 text-center text-[11px] text-slate-400">
        Powered by <span className="font-medium text-amber-700">Bluemvmt</span>
      </p>
    </div>
  );

  const renderModeTabs = (expanded = false) => {
    const tabSize = expanded
      ? 'gap-1 px-2.5 py-1 text-xs'
      : isPanelCollapsed
        ? 'gap-1 px-1.5 py-1 text-[10px]'
        : 'gap-1 px-2 py-1 text-[11px]';

    return (
    <div
      className="flex shrink-0 rounded-lg bg-slate-100 p-0.5 gap-0.5"
      role="tablist"
      aria-label="Panel mode"
    >
      <button
        type="button"
        role="tab"
        aria-selected={!isCommsMode}
        onClick={() => onPanelModeChange?.('assistant')}
        className={`flex items-center justify-center rounded-md font-medium transition-colors ${tabSize} ${
          !isCommsMode
            ? 'bg-white text-slate-800 shadow-sm'
            : 'text-slate-500 hover:text-slate-700'
        }`}
        title="Chat"
      >
        <MessageCircle className="h-3 w-3 shrink-0" aria-hidden />
        <span>Chat</span>
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={isCommsMode}
        onClick={() => onPanelModeChange?.('comms')}
        className={`flex items-center justify-center rounded-md font-medium transition-colors ${tabSize} ${
          isCommsMode
            ? 'bg-white text-slate-800 shadow-sm'
            : 'text-slate-500 hover:text-slate-700'
        }`}
        title="Exercise comms"
      >
        <Headset className="h-3 w-3 shrink-0" aria-hidden />
        <span>Comms</span>
      </button>
    </div>
    );
  };

  const renderHeader = (expanded = false) => (
    <div className={`flex items-center justify-between gap-2 border-b border-slate-100 bg-white ${
      expanded ? 'px-5 py-4' : 'px-3 py-3 md:px-4'
    }`}>
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {!isPanelCollapsed && (
          <h3 className={`truncate font-semibold text-slate-800 ${expanded ? 'text-base' : 'text-sm md:text-[15px]'}`}>
            Digital Ocean
          </h3>
        )}
        {renderModeTabs(expanded)}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {!isCommsMode && (
          <button
            onClick={handleRefreshChat}
            className={`rounded-md font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 ${
              isPanelCollapsed ? 'p-1.5' : 'px-2 py-1 text-xs'
            }`}
            title="New chat"
          >
            {isPanelCollapsed ? '+' : 'New chat'}
          </button>
        )}
        {onTogglePanelCollapse && (
          <button
            type="button"
            onClick={onTogglePanelCollapse}
            className="rounded-full p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
            title={isPanelCollapsed ? 'Expand panel' : 'Collapse panel'}
            aria-label={isPanelCollapsed ? 'Expand panel' : 'Collapse panel'}
          >
            {isPanelCollapsed ? (
              <ChevronLeft className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
        )}
        <button
          onClick={expanded ? () => setIsExpanded(false) : handleExpandChat}
          className="rounded-full p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          title={expanded ? 'Close expanded view' : 'Expand'}
          aria-label={expanded ? 'Close expanded view' : 'Expand'}
        >
          {expanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );

  const renderBody = (expanded = false) => {
    if (isCommsMode) {
      return (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-slate-100/90">
          <ExerciseCommsPanel embedded />
        </div>
      );
    }
    return renderMessages(expanded);
  };

  const chatShell = (expanded = false) => (
    <div className={`flex h-full w-full flex-col overflow-hidden bg-white text-slate-900 [color-scheme:light] ${
      expanded
        ? 'max-h-[90vh] rounded-2xl border border-slate-200/80 shadow-xl'
        : 'rounded-none border-0 shadow-none'
    }`}>
      {renderHeader(expanded)}
      {renderBody(expanded)}
      {!isCommsMode && renderInput(expanded)}
    </div>
  );

  return (
    <>
      <div className="h-full w-full">
        {chatShell(false)}
      </div>
      {isExpanded && createPortal(
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm md:p-8">
          <div className="h-full w-full max-w-2xl max-h-[90vh]">
            {chatShell(true)}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default Chatbot;
